import { useCallback, useEffect, useState } from 'react';

/**
 * useAsync — Run an async loader and expose its lifecycle as state.
 *
 * Design notes
 * ────────────
 * `loading` is *derived*, not stored. Each settled result is tagged with the
 * dependency key that produced it, and the hook reports "loading" whenever the
 * stored tag does not match the current key. That has two benefits over the
 * common `setLoading(true)` pattern:
 *
 *   • No state update inside the effect just to enter the loading state, which
 *     would trigger an extra render pass on every dependency change.
 *   • Stale responses are structurally impossible. A slow request that resolves
 *     after the dependencies moved on writes a tag that no longer matches, so its
 *     data is never shown.
 *
 * Requests are also aborted on dependency change and unmount, so an abandoned
 * fetch stops consuming the connection rather than merely being ignored.
 *
 * @param {(signal: AbortSignal) => Promise<T>} loader
 * @param {ReadonlyArray<unknown>} deps Re-runs the loader when these change.
 * @returns {{ data: T|null, error: Error|null, loading: boolean, reload: () => void }}
 * @template T
 */
export function useAsync(loader, deps = []) {
  const [nonce, setNonce] = useState(0);

  // Deps are primitives by convention here, so a plain join is a stable and much
  // cheaper key than JSON.stringify. The nonce lets reload() re-key an otherwise
  // unchanged dependency list.
  const key = `${deps.map((dep) => String(dep)).join('\u0000')}#${nonce}`;

  const [settled, setSettled] = useState({ key: null, data: null, error: null });

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    loader(controller.signal)
      .then((data) => {
        if (active) setSettled({ key, data, error: null });
      })
      .catch((error) => {
        // An abort is the expected outcome of navigating away, not a failure.
        if (active && error.name !== 'AbortError') setSettled({ key, data: null, error });
      });

    return () => {
      active = false;
      controller.abort();
    };
    // `loader` is intentionally excluded. Callers pass an inline closure that is
    // recreated every render, so including it would refetch on each render. The
    // closure only ever reads the values already encoded in `key`, so the
    // captured version and the current one are equivalent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const loading = settled.key !== key;

  return {
    data: loading ? null : settled.data,
    error: loading ? null : settled.error,
    loading,
    reload,
  };
}

/**
 * Debounce a rapidly changing value — used for the search box and the EMI
 * calculator slider so neither fires a request per keystroke or per pixel.
 *
 * @param {T} value
 * @param {number} [delay=300]
 * @returns {T}
 * @template T
 */
export function useDebounced(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}
